import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedElement,
  updateSelectedInteraction,
} from "../../../app/slices/TrainingSlice";
import {
  ICharacterPartDefinition,
  ISetCharacterParts,
} from "../../../models/interactions/SetCharacterParts";
import ActorPropDropdown from "../lookat/ActorPropDropdown";
import { useState } from "react";
import CharacterPartLibrary from "./CharacterPartLibrary";
import { CharacterPartGenderedPairDetailsDTO } from "../../../dto/CharacterPartGenderedPairDetailsDTO";
import CharacterPartDefinitionsPreview from "./CharacterPartDefinitionsPreview";
import { setCachedCharacterPartDetails } from "../../../app/slices/CharacterPartsSlice";
import { GetNewGuid } from "../../../helpers/GuidHelper";
import PiBasicQuizAddAnswerButtonV2 from "../components/v2/PiBasicQuizAddAnswerButtonV2";
import useSetCharacterParts from "./SetCharacterParts.hook";

export default function SetCharacterPartsPreviewBody() {
  const dispatch = useDispatch();
  const interaction = useSelector(selectSelectedElement) as ISetCharacterParts;
  const [isOpen, setIsOpen] = useState(false);

  const _ = useSetCharacterParts(); // fetches missing character part details

  const updateInteraction = (field: keyof ISetCharacterParts, value: any) => {
    dispatch(updateSelectedInteraction({ ...interaction, [field]: value }));
  };

  const onVariantSelected = (id: string, variantId: string) => {
    const index = interaction.partDefinitions.findIndex((p) => p.id === id);
    const copy = [...interaction.partDefinitions];
    copy[index] = { ...copy[index], variantId };
    updateInteraction("partDefinitions", copy);
  };

  const onDeleteCharacterPartDefinition = (id: string) => {
    const index = interaction.partDefinitions.findIndex((p) => p.id === id);
    const copy = [...interaction.partDefinitions];
    copy.splice(index, 1);
    updateInteraction("partDefinitions", copy);
  };

  // 1. actor dropdown
  const actorDropdown = (
    <ActorPropDropdown
      label="Target"
      onClick={(id) => updateInteraction("npcId", id)}
      selectedObjectId={interaction.npcId}
    />
  );

  // 2. part definitions
  const partDefinitions = (
    <div className="flex-1 min-h-0 flex flex-col">
      <CharacterPartDefinitionsPreview
        interaction={interaction}
        onVariantSelected={onVariantSelected}
        onDelete={onDeleteCharacterPartDefinition}
      />
    </div>
  );

  const addButton = (
    <PiBasicQuizAddAnswerButtonV2
      onClick={() => setIsOpen(!isOpen)}
      text="Add Pair"
    />
  );

  const onAddCharacterPart = (
    characterPartDetails: CharacterPartGenderedPairDetailsDTO
  ) => {
    const definition: ICharacterPartDefinition = {
      id: GetNewGuid(),
      partId: characterPartDetails.name,
      partType: characterPartDetails.characterPartType,
      malePartId: characterPartDetails.malePartId,
      femalePartId: characterPartDetails.femalePartId,
      variantId: characterPartDetails.variants[0],
    };

    // add to the interaction
    updateInteraction("partDefinitions", [
      ...(interaction.partDefinitions || []),
      definition,
    ]);

    // add the details to the cached char part details
    dispatch(
      setCachedCharacterPartDetails({
        characterPartName: characterPartDetails.name,
        characterPartDetails: characterPartDetails,
      })
    );
  };

  return (
    <div className="flex flex-col w-full h-full gap-[12px]">
      {actorDropdown}
      {partDefinitions}
      {addButton}
      {isOpen && (
        <CharacterPartLibrary
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title="Character Part Library"
          width="calc(90vw / var(--app-zoom, 1))"
          height="calc(90vh / var(--app-zoom, 1))"
          onSubmit={onAddCharacterPart}
        />
      )}
    </div>
  );
}
